import { useState } from 'react';
import { Search, Calendar, CheckCircle } from 'lucide-react';
import { API_BASE } from '../utils/supabase/info';
import { devError } from '../utils/logger';


interface TermsHistoryEntry {
  version: string;
  acceptedAt: string;
}

interface AdminTermsHistoryProps {
  accessToken: string;
}

export function AdminTermsHistory({ accessToken }: AdminTermsHistoryProps) {
  const [userId, setUserId] = useState('');
  const [history, setHistory] = useState<TermsHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchHistory = async () => {
    if (!userId.trim()) {
      setError('Vennligst oppgi en bruker-ID');
      return;
    }

    setLoading(true);
    setError('');
    setHistory([]);

    try {
      const response = await fetch(
        `${API_BASE}/terms-history/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Kunne ikke hente historikk');
        setLoading(false);
        return;
      }

      const data = await response.json();
      setHistory(data.history || []);
      
      if (data.history.length === 0) {
        setError('Ingen historikk funnet for denne brukeren');
      }
    } catch (err) {
      devError('Error fetching terms history:', err);
      setError('Kunne ikke hente historikk. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-purple-100">
      <h2 className="text-2xl mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
        Vilkårshistorikk (Admin)
      </h2>

      <p className="text-gray-600 mb-6">
        Søk etter en brukers historikk over godkjente vilkår. Dette viser alle tidspunkter brukeren har godtatt vilkårene.
      </p>

      {/* Search form */}
      <div className="flex gap-3 mb-6">
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="Bruker-ID"
          className="flex-1 px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <button
          onClick={fetchHistory}
          disabled={loading}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Search className="w-5 h-5" />
          {loading ? 'Søker...' : 'Søk'}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}

      {/* History list */}
      {history.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg text-purple-900 mb-3">
            Historikk for bruker: <span className="font-mono text-sm">{userId}</span>
          </h3>
          {history.map((entry, index) => (
            <div
              key={index}
              className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-100"
            >
              <div className="p-2 bg-white rounded-full">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-purple-900">
                  <strong>Versjon {entry.version}</strong>
                </p>
                <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {new Date(entry.acceptedAt).toLocaleDateString('nb-NO', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
              {index === 0 && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  Siste
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
